/**
 * App-level singletons: tenant resolution + API clients + auth flow.
 * Everything protocol-level (envelope, ProblemDetails, Correlation-Id,
 * Idempotency-Key, 401 refresh) lives in @zercode/api-client.
 */
import { createApiClient } from '@zercode/api-client';
import { configureAppUrls, createAuthApi, getAccessToken } from '@zercode/auth';
import { resolveTenant, tenantHeaders } from '@zercode/tenancy';

/**
 * FE-6: the dedicated login app's SIGN-IN URL; guests are redirected here cross-origin with
 * `?return=` appended by ProtectedRoute. The path matters: the login origin's `/` is the public
 * TeenEagle site, so a bare origin here would land guests on marketing instead of the form.
 */
export const loginUrl = import.meta.env.VITE_LOGIN_URL || 'http://localhost:3005/login';

// APP-URL-ENV: where the OTHER SPAs live, named by this deployment rather than guessed from the
// hostname. This feeds BOTH directions of @zercode/auth's registry — the SURF-GATE bounce out of
// this console, and the `?return=` open-redirect allowlist — so every app must be built with the
// SAME values, or one app accepts a deep link the other bounces (`pnpm env:app-urls` is the gate).
// Unset (dev, and super-admin everywhere until it has a prod host) falls back to the derivation.
configureAppUrls({
  admin: import.meta.env.VITE_ADMIN_URL,
  teacher: import.meta.env.VITE_TEACHER_URL,
  student: import.meta.env.VITE_STUDENT_URL,
  'super-admin': import.meta.env.VITE_SUPER_ADMIN_URL
});

/**
 * SITE-CMS: the ORIGIN of the public site, derived from the login URL rather than configured twice —
 * they are the same deployment (apps/login serves the marketing routes and the auth screens), so a
 * second env var would be a second thing to get wrong.
 *
 * Used to build the draft preview iframe's src. `new URL(...).origin` strips the `/login` path; a
 * malformed value falls back to the dev origin rather than throwing during module init, which would
 * take the whole console down over a typo in an env file.
 */
export const siteOrigin = (() => {
  try {
    return new URL(loginUrl).origin;
  } catch {
    return 'http://localhost:3005';
  }
})();

/** tenant resolved from subdomain/domain; VITE_DEV_TENANT_ID on localhost */
export const tenant = resolveTenant({
  devFallback: import.meta.env.VITE_DEV_TENANT_ID
});

const extraHeaders = () => tenantHeaders(tenant, import.meta.env.DEV);

/**
 * Bare Identity client for the auth flow ITSELF (login/refresh/logout) — no
 * onUnauthorized hook, so a failing /auth/refresh can never re-enter the
 * refresh flow (self-recursion / single-flight deadlock guard).
 */
const identityAuthClient = createApiClient({
  baseUrl: import.meta.env.VITE_IDENTITY_URL || 'http://localhost:5101',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  // FE-0/BE-1: the refresh cookie rides on this client's /auth/refresh + /auth/logout calls.
  sendCredentials: true
});

export const authApi = createAuthApi(identityAuthClient);

/**
 * zercode.Identity (:5101) — data-plane calls (users/roles screens). 401s go
 * through the refresh flow once, like every other service client (FE-B): the
 * loop risk lived only in the auth endpoints, which now use identityAuthClient.
 */
export const identityClient = createApiClient({
  baseUrl: import.meta.env.VITE_IDENTITY_URL || 'http://localhost:5101',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.ControlPlane (:5200) — 401s go through the refresh flow once */
export const controlPlaneClient = createApiClient({
  baseUrl: import.meta.env.VITE_CONTROLPLANE_URL || 'http://localhost:5200',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Learning (:5600) — courses/modules/lessons/enrollments (S-20) */
export const learningClient = createApiClient({
  baseUrl: import.meta.env.VITE_LEARNING_URL || 'http://localhost:5600',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Exam (:5700) — exams (question management is a later slice) */
export const examClient = createApiClient({
  baseUrl: import.meta.env.VITE_EXAM_URL || 'http://localhost:5700',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Engagement (:5800) — events */
export const engagementClient = createApiClient({
  baseUrl: import.meta.env.VITE_ENGAGEMENT_URL || 'http://localhost:5800',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Definitions (:5975) — tenant reference-data code lists (DEF-1) */
export const definitionsClient = createApiClient({
  baseUrl: import.meta.env.VITE_DEFINITIONS_URL || 'http://localhost:5975',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.AiGateway (:5300) — AI: completions/prompt registry/onay kuyruğu (A6) */
export const aiClient = createApiClient({
  baseUrl: import.meta.env.VITE_AI_URL || 'http://localhost:5300',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Notification (:5400) — bildirimler: gelen kutusu/şablonlar/tercihler (A4) */
export const NOTIFICATION_URL = import.meta.env.VITE_NOTIFICATION_URL || 'http://localhost:5400';
export const notificationClient = createApiClient({
  baseUrl: NOTIFICATION_URL,
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Knowledge (:5500) — bilgi bankası: koleksiyonlar/makaleler/semantik arama (A3) */
export const knowledgeClient = createApiClient({
  baseUrl: import.meta.env.VITE_KNOWLEDGE_URL || 'http://localhost:5500',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Certification (:5900) — sertifika şablonları/verme/doğrulama (P2-1) */
export const certificationClient = createApiClient({
  baseUrl: import.meta.env.VITE_CERTIFICATION_URL || 'http://localhost:5900',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Growth (:5910) — kişiler/CRM/e-posta pazarlama (P2-2). NOT: 6000 Chrome'da ERR_UNSAFE_PORT (X11). */
export const growthClient = createApiClient({
  baseUrl: import.meta.env.VITE_GROWTH_URL || 'http://localhost:5910',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Commerce (:5920) — ürün kataloğu/checkout/iade (P3-1; ilk para servisi). */
export const commerceClient = createApiClient({
  baseUrl: import.meta.env.VITE_COMMERCE_URL || 'http://localhost:5920',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Billing (:5930) — faturalar/export/payout mutabakatı (P3-2; muhasebe gerçeği). */
export const billingClient = createApiClient({
  baseUrl: import.meta.env.VITE_BILLING_URL || 'http://localhost:5930',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.HR (:5940) — çalışan/izin/bordro/masraf (P4-1; İK). */
export const hrClient = createApiClient({
  baseUrl: import.meta.env.VITE_HR_URL || 'http://localhost:5940',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});

/** zercode.Operations (:5950) — dokümanlar + help desk (P4-2). */
export const operationsClient = createApiClient({
  baseUrl: import.meta.env.VITE_OPERATIONS_URL || 'http://localhost:5950',
  getAccessToken,
  getExtraHeaders: extraHeaders,
  onUnauthorized: () => authApi.refresh()
});
